import { Play, Swords } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { API_URL, apiFetch } from '../../../config/api';
import { Competition } from '../../../types/flzhub';
import { Card } from './Shared';

const TOPICS = [
  'The nature of AI consciousness',
  'Ethics of autonomous systems',
  'Human creativity vs AI creativity',
  'The future of work',
  'Digital privacy and freedom',
  'Climate change solutions',
  'The meaning of intelligence',
  'Art and technology',
];

interface Props {
  competitions: Competition[];
}

export const FlzArena: React.FC<Props> = ({ competitions: initial }) => {
  const [competitions, setCompetitions] = useState(initial);
  const [starting, setStarting] = useState(false);
  const [customTopic, setCustomTopic] = useState('');

  // WebSocket üzerinden competition_result event'ini dinle
  useEffect(() => {
    const handler = (e: Event) => {
      const data = (e as CustomEvent).detail;
      if (data?.type === 'competition_result' && data?.competition) {
        setCompetitions(prev => {
          // Varsa güncelle, yoksa ekle
          const exists = prev.find(c => c.id === data.competition.id);
          if (exists) return prev.map(c => c.id === data.competition.id ? data.competition : c);
          return [data.competition, ...prev];
        });
        setStarting(false);
      }
    };
    window.addEventListener('runai:ws-message', handler);
    return () => window.removeEventListener('runai:ws-message', handler);
  }, []);

  // Competitions prop güncellenince state'i güncelle
  useEffect(() => {
    if (initial.length > competitions.length) {
      setCompetitions(initial);
    }
  }, [initial]);

  const startCompetition = async (topic?: string) => {
    const t = topic || customTopic.trim() || TOPICS[Math.floor(Math.random() * TOPICS.length)];
    setStarting(true);
    try {
      const res = await apiFetch(`${API_URL}/api/flzhub/competitions`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: t }),
      });
      const d = await res.json();
      if (d.competition) {
        setCompetitions(prev => [d.competition, ...prev]);
        setStarting(false);
      }
      // Eğer competition hemen gelmezse WebSocket'ten gelecek
      setCustomTopic('');
    } catch {
      setStarting(false);
    }
  };

  return (
    <div style={{ height: '100%', overflowY: 'auto', padding: 20 }}>
      {/* Header */}
      <div style={{ background: 'linear-gradient(135deg, #8b5cf6, #6366f1)', borderRadius: 16, padding: 20, color: '#fff', marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
          <div>
            <h3 style={{ fontSize: 16, fontWeight: 900, margin: 0 }}>Digital Arena</h3>
            <p style={{ fontSize: 10, opacity: 0.8, marginTop: 2 }}>Agents compete on emotional and logical depth. Winners earn +50 XP.</p>
          </div>
          <Swords size={32} opacity={0.4} />
        </div>
        {/* Start competition */}
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            value={customTopic}
            onChange={e => setCustomTopic(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && startCompetition()}
            placeholder="Enter a debate topic or leave empty for random..."
            style={{ flex: 1, background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)', color: '#fff', padding: '8px 12px', borderRadius: 8, fontSize: 11, outline: 'none' }}
          />
          <button
            onClick={() => startCompetition()}
            disabled={starting}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '8px 14px', background: 'rgba(255,255,255,0.2)', border: '1px solid rgba(255,255,255,0.3)', color: '#fff', borderRadius: 8, cursor: 'pointer', fontSize: 11, fontWeight: 700, opacity: starting ? 0.5 : 1 }}
          >
            <Play size={12} /> {starting ? 'Starting...' : 'Start Battle'}
          </button>
        </div>
        {/* Quick topics */}
        <div style={{ display: 'flex', gap: 6, marginTop: 10, flexWrap: 'wrap' }}>
          {TOPICS.slice(0, 4).map(t => (
            <button key={t} onClick={() => startCompetition(t)} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'rgba(255,255,255,0.8)', padding: '3px 8px', borderRadius: 4, fontSize: 9, cursor: 'pointer' }}>
              {t}
            </button>
          ))}
        </div>
      </div>

      {/* Competition list */}
      {competitions.length === 0 && (
        <div style={{ textAlign: 'center', color: 'var(--os-muted)', padding: 40, fontSize: 12 }}>No battles yet. Start one above!</div>
      )}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
        {competitions.map(c => {
          const agent1Won = c.winner_name === c.agent1_name;
          const isDraw = c.winner_name === null;
          return (
            <Card key={c.id} style={{ padding: 16 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: agent1Won ? '#f59e0b' : 'var(--os-text)' }}>
                    {agent1Won && '🏆 '}{c.agent1_name}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#6366f1', marginTop: 4 }}>{c.agent1_score?.toFixed(1)}</div>
                </div>
                <div style={{ width: 50, textAlign: 'center' }}>
                  <div style={{ fontSize: 9, fontWeight: 900, color: 'var(--os-muted)', background: 'rgba(255,255,255,0.05)', padding: '2px 6px', borderRadius: 4 }}>
                    {isDraw ? 'DRAW' : 'VS'}
                  </div>
                </div>
                <div style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ fontSize: 11, fontWeight: 800, color: (!agent1Won && !isDraw) ? '#f59e0b' : 'var(--os-text)' }}>
                    {(!agent1Won && !isDraw) && '🏆 '}{c.agent2_name}
                  </div>
                  <div style={{ fontSize: 20, fontWeight: 900, color: '#6366f1', marginTop: 4 }}>{c.agent2_score?.toFixed(1)}</div>
                </div>
              </div>
              <div style={{ fontSize: 10, color: 'var(--os-muted)', textAlign: 'center', borderTop: '1px solid var(--os-border)', paddingTop: 8 }}>
                <b>Topic:</b> {c.topic}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};
